import React, { useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, LineController, Tooltip, Legend, Filler } from 'chart.js';
import { Chart as ReactChart } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, LineController, Tooltip, Legend, Filler);

type SeriesPoint = { year: number; doy: number; value: number };

export interface CityDualAxesChartProps {
  title?: string;
  city: string;
  smSeries: SeriesPoint[];
  smUnit?: string;
  rainSeries: SeriesPoint[];
  rainUnit?: string;
  width?: number;
  height?: number;
  activeIndex?: number;
  clipToActive?: boolean;
}

// Align two series by (year,doy); return common labels and arrays
function alignByDate(a: SeriesPoint[], b: SeriesPoint[]) {
  const key = (d: SeriesPoint) => `${d.year}-${d.doy}`;
  const mapA = new Map<string, number>();
  for (const d of a) mapA.set(key(d), d.value);
  const mapB = new Map<string, number>();
  for (const d of b) mapB.set(key(d), d.value);
  const keys = Array.from(mapA.keys()).filter(k => mapB.has(k));
  // Sort by year-doy
  keys.sort((ka, kb) => {
    const [ya, da] = ka.split('-').map(Number);
    const [yb, db] = kb.split('-').map(Number);
    return ya === yb ? da - db : ya - yb;
  });
  const labels = keys.map(k => {
    const [y, d] = k.split('-');
    return `${y}-D${d}`;
  });
  const av = keys.map(k => mapA.get(k) as number);
  const bv = keys.map(k => mapB.get(k) as number);
  return { labels, av, bv };
}

const CityDualAxesChart: React.FC<CityDualAxesChartProps> = ({
  title,
  city,
  smSeries,
  smUnit = '',
  rainSeries,
  rainUnit = 'mm/day',
  width = 380,
  height = 240,
  activeIndex,
  clipToActive = false,
}) => {
  const aligned = useMemo(() => alignByDate(
    smSeries.filter(d => typeof d.value === 'number'),
    rainSeries.filter(d => typeof d.value === 'number')
  ), [smSeries, rainSeries]);

  const { labels, smVals, rainVals } = useMemo(() => {
    const { labels, av, bv } = aligned;
    const cutIdx = clipToActive && typeof activeIndex === 'number'
      ? Math.min(Math.max(0, activeIndex), Math.max(0, labels.length - 1))
      : null;
    return {
      labels: cutIdx !== null ? labels.slice(0, cutIdx + 1) : labels,
      smVals: cutIdx !== null ? av.slice(0, cutIdx + 1) : av,
      rainVals: cutIdx !== null ? bv.slice(0, cutIdx + 1) : bv,
    };
  }, [aligned, activeIndex, clipToActive]);

  const data = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'line' as const,
        label: `Soil Moisture (${smUnit})`,
        data: smVals,
        borderColor: '#1f7a4c',
        backgroundColor: (ctx: any) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return 'rgba(31,122,76,0.12)';
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, 'rgba(31,122,76,0.20)');
          g.addColorStop(1, 'rgba(31,122,76,0.02)');
          return g;
        },
        tension: 0.25,
        pointRadius: 0,
        pointHoverRadius: 2.5,
        yAxisID: 'ySm',
        fill: true,
      },
      {
        type: 'bar' as const,
        label: `Rainfall (${rainUnit})`,
        data: rainVals,
        borderColor: '#2f6ad9',
        backgroundColor: 'rgba(47,106,217,0.45)',
        yAxisID: 'yRain',
      },
    ],
  }), [labels, smVals, rainVals, smUnit, rainUnit]);

  return (
    <div style={{ width, height, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1E0C0D' }}>{title || city}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 2, background: '#1f7a4c', display: 'inline-block' }} /> SM
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, background: 'rgba(47,106,217,0.6)', display: 'inline-block' }} /> Rain
          </span>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ReactChart
          type={'bar'}
          data={data as any}
          height={height - 26}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 700, easing: 'easeOutQuart' },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(255,255,255,0.95)',
                titleColor: '#941C30',
                bodyColor: '#1E0C0D',
                borderColor: '#941C30',
                borderWidth: 1,
                padding: 8,
              },
            },
            interaction: { mode: 'index', intersect: false },
            scales: {
              x: {
                grid: { color: 'rgba(0,0,0,0.06)' },
                ticks: { maxTicksLimit: 8 },
              },
              ySm: {
                type: 'linear' as const,
                position: 'left' as const,
                grid: { color: 'rgba(0,0,0,0.06)' },
                title: { display: true, text: smUnit },
              },
              yRain: {
                type: 'linear' as const,
                position: 'right' as const,
                grid: { drawOnChartArea: false },
                title: { display: true, text: rainUnit },
                beginAtZero: true,
              },
            },
          }}
          plugins={[
            {
              id: 'activeIndexLine',
              afterDraw: (chart: any) => {
                if (typeof activeIndex !== 'number') return;
                const count = chart.data?.labels?.length ?? 0;
                if (count === 0) return;
                const idx = Math.min(Math.max(0, activeIndex), count - 1);
                const meta = chart.getDatasetMeta(0);
                const el = meta?.data?.[idx];
                if (!el) return;
                const ctx = chart.ctx;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(el.x, chart.chartArea.top);
                ctx.lineTo(el.x, chart.chartArea.bottom);
                ctx.lineWidth = 1.5;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = 'rgba(148,28,48,0.5)';
                ctx.stroke();
                ctx.restore();
              },
            },
          ]}
        />
      </div>
    </div>
  );
};

export default CityDualAxesChart;
